import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { useAuthStore } from '../stores/authStore';
import { colors } from '../styles/colors';
import { IconClose } from './icons';

interface ReportModalProps {
  targetId: string;
  targetNickname: string;
  /** Where the report was initiated from (e.g. 'message', 'dm', 'board', 'map', 'nearby') */
  contentType?: string;
  /** Preview of the content being reported */
  contentPreview?: string;
  /** Timestamp of the content (ms) for traceability */
  contentTimestamp?: number;
  onClose: () => void;
}

const REPORT_REASONS = [
  { key: 'spam', labelKo: '스팸/광고', labelEn: 'Spam/Advertising' },
  { key: 'abuse', labelKo: '욕설/비하', labelEn: 'Abuse/Harassment' },
  { key: 'sexual', labelKo: '성적 콘텐츠', labelEn: 'Sexual Content' },
  { key: 'fraud', labelKo: '사기/사칭', labelEn: 'Fraud/Impersonation' },
  { key: 'other', labelKo: '기타', labelEn: 'Other' },
];

const CONTENT_TYPE_LABELS: Record<string, { ko: string; en: string }> = {
  message: { ko: '채팅 메시지', en: 'Chat Message' },
  dm: { ko: '쪽지', en: 'DM' },
  board: { ko: '게시물', en: 'Board Post' },
  comment: { ko: '댓글', en: 'Comment' },
  map: { ko: '구글맵', en: 'Google Map' },
  nearby: { ko: '최근 사용자', en: 'Recent Users' },
  profile: { ko: '프로필', en: 'Profile' },
};

export default function ReportModal({ targetId, targetNickname, contentType, contentPreview, contentTimestamp, onClose }: ReportModalProps) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const isKo = i18n.language === 'ko';

  const handleSubmit = async () => {
    if (!reason || !user) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'reports'), {
        reporterId: user.uid,
        reporterNickname: user.nickname,
        targetId,
        targetNickname,
        reason,
        detail: detail.trim(),
        contentType: contentType ?? '',
        contentPreview: contentPreview ?? '',
        contentTimestamp: contentTimestamp ?? null,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setDone(true);
    } catch (err) {
      console.error('[Report] Failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = contentType ? (isKo ? CONTENT_TYPE_LABELS[contentType]?.ko : CONTENT_TYPE_LABELS[contentType]?.en) ?? contentType : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl"
        style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: `1px solid ${colors.border}` }}>
          <h3 className="text-sm font-bold" style={{ color: colors.text }}>
            {t('profile.report')} - {targetNickname}
          </h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-white/10">
            <IconClose size={18} color={colors.textMuted} />
          </button>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8">
            <span className="text-3xl">✅</span>
            <p className="text-sm font-semibold" style={{ color: colors.text }}>
              {isKo ? '신고가 접수되었습니다.' : 'Report submitted.'}
            </p>
            <button onClick={onClose}
              className="mt-2 rounded-lg px-6 py-2 text-sm font-bold text-white"
              style={{ backgroundColor: colors.primary }}>
              {t('common.confirm')}
            </button>
          </div>
        ) : (
          <div className="px-4 py-4">
            {/* Content context */}
            {(typeLabel || contentPreview) && (
              <div className="mb-3 rounded-lg px-3 py-2" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                {typeLabel && (
                  <p className="text-[10px] font-bold" style={{ color: colors.textMuted }}>
                    {isKo ? '신고 대상' : 'Reported Content'}: {typeLabel}
                  </p>
                )}
                {contentPreview && (
                  <p className="mt-0.5 text-xs line-clamp-3" style={{ color: colors.textSecondary }}>
                    {contentPreview}
                  </p>
                )}
              </div>
            )}

            <p className="mb-3 text-xs" style={{ color: colors.textMuted }}>
              {isKo ? '신고 사유를 선택해주세요:' : 'Select a reason:'}
            </p>

            <div className="flex flex-col gap-1.5">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setReason(r.key)}
                  className="rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: reason === r.key ? `${colors.primary}20` : colors.card,
                    color: reason === r.key ? colors.primary : colors.text,
                    border: `1.5px solid ${reason === r.key ? colors.primary : 'transparent'}`,
                  }}
                >
                  {isKo ? r.labelKo : r.labelEn}
                </button>
              ))}
            </div>

            {reason === 'other' && (
              <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder={isKo ? '상세 내용을 입력하세요...' : 'Describe the issue...'}
                className="mt-3 w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                style={{ borderColor: colors.border, color: colors.text }}
                rows={3}
              />
            )}

            <div className="mt-4 flex gap-2">
              <button onClick={onClose}
                className="flex-1 rounded-lg py-2.5 text-sm font-semibold"
                style={{ backgroundColor: colors.card, color: colors.textMuted }}>
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason || submitting}
                className="flex-1 rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: colors.warning }}>
                {submitting ? '...' : t('profile.report')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
